import { useId } from "react";
import styles from "./Field.module.css";

interface SelectOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

interface SelectProps<T extends string> {
  label: string;
  value: T;
  onChange: (next: T) => void;
  options: readonly SelectOption<T>[];
  error?: string;
  helper?: string;
  disabled?: boolean;
  id?: string;
}

/**
 * Select — labeled dropdown. Same visual anatomy as <Field>.
 *
 * Native <select> intentionally; a custom listbox is a lot of ARIA
 * surface to get wrong for a form control that already works. The
 * only styling that matters is the trigger, and that matches Field.
 */
export function Select<T extends string>({
  label,
  value,
  onChange,
  options,
  error,
  helper,
  disabled = false,
  id,
}: SelectProps<T>) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  return (
    <div className={styles.field}>
      <label htmlFor={fieldId} className={styles.label}>
        {label}
      </label>
      <select
        id={fieldId}
        className={`${styles.input} ${error ? styles.error : ""}`}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        disabled={disabled}
        aria-invalid={!!error}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      <div className={styles.footer}>
        {error ? (
          <span className={styles.errorText} role="alert">
            {error}
          </span>
        ) : helper ? (
          <span className={styles.helper}>{helper}</span>
        ) : (
          <span aria-hidden />
        )}
      </div>
    </div>
  );
}
